import { useMemo } from 'react';

import transformOrganizationSubscriptions from '~/utils/subscriberPreferences/transformOrganizationSubscription';
import transformSubscriberPreferences from '~/utils/subscriberPreferences/transformSubscriberPreferences';

import useOrganizationSubscriptions from './useStaticOrganizationSubscriptions';
import useStaticSubscriptionPreferences from './useStaticSubscriptionPreferences';

export const usePublicationPreferences = (subscriberId: string, isLoggedIn: boolean) => {
  const {
    data: subscriptionPreferences,
    isLoading: isLoadingSubscriptionPreferences,
    refetch: refetchPreferences,
  } = useStaticSubscriptionPreferences({
    enabled: !!subscriberId && isLoggedIn,
    subscriberId: subscriberId,
  });

  const {
    data: organizationSubscriptions,
    isLoading: isLoadingOrganizationSubscriptions,
    refetch: refetchOrganizationSubscriptions,
  } = useOrganizationSubscriptions({
    enabled: !!subscriberId && isLoggedIn,
    subscriberId: subscriberId,
  });

  const isLoading = isLoadingSubscriptionPreferences || isLoadingOrganizationSubscriptions;

  /**
   * Sort preferences so that organization subscriptions come first
   * Organization subscriptions are boolean type with attrs.data.type === 'publication'
   */
  const preferences = useMemo(() => {
    return [
      ...(organizationSubscriptions ? transformOrganizationSubscriptions(organizationSubscriptions) : []),
      ...(subscriptionPreferences ? transformSubscriberPreferences(subscriptionPreferences) : []),
    ].sort((a, b) => {
      const aIsOrganization = a.type === 'boolean' && a.attrs?.data?.type === 'publication';
      const bIsOrganization = b.type === 'boolean' && b.attrs?.data?.type === 'publication';

      if (aIsOrganization && !bIsOrganization) return -1;
      if (!aIsOrganization && bIsOrganization) return 1;
      return 0;
    });
  }, [organizationSubscriptions, subscriptionPreferences]);

  const hasPreferences = preferences.length > 0;

  return {
    isLoading,
    preferences,
    hasPreferences,
    refetch: () => {
      refetchPreferences();
      refetchOrganizationSubscriptions();
    },
  };
};
